/// <reference types="vite-plugin-svgr/client" />
/**
 * Holds all the controls for the preview panel.
 * Toggle row: bottom-left of preview pane (absolute).
 * @module PreviewControlBar
 * @category Display Components
 */
import { clientConfig, colors, focusVisible, text } from "@quickpoint.me/shared";
import { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";

import Monitor from "../../icons/desktop-preview.svg?react";
import Phone from "../../icons/smartphone-line.svg?react";
import FullSizePreview from "../../icons/full-size-preview.svg?react";

import { HostId } from "../../state/atoms";
import { ShowLandscapePreview, ShowPortraitPreview } from "../../state/uiAtoms";

const Tooltip = styled.div<{ $x: number; $y: number; $visible: boolean }>`
  position: fixed;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  transform: translate(-50%, -100%);
  padding: 6px 10px;
  background-color: ${colors.tufte.black};
  color: ${colors.white};
  font-family: ${(props) => props.theme.font};
  font-size: 0.8rem;
  font-weight: 400;
  border-radius: 4px;
  white-space: nowrap;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
  transition: opacity 0.15s ease;
  z-index: 9999;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${colors.tufte.black};
  }
`;

const ToggleRow = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(0, 0, 0, 0.12);
  margin: 0 4px;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  /* 48px gold-standard target; the visual icon stays 18px */
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  background: ${(props) =>
    props.$active ? "rgba(0, 122, 204, 0.15)" : "transparent"};
  border: 1.5px solid ${(props) =>
    props.$active ? colors.alabaster.active : "transparent"};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  svg path {
    /* off-state ink >=3:1 on the bar (0.4 black was 2.5:1) */
    fill: ${(props) =>
      props.$active ? colors.alabaster.active : colors.textSafe.grey};
    transition: fill 0.15s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &:hover svg path {
    fill: ${(props) =>
      props.$active ? colors.alabaster.active : "#1a1a1a"};
  }

  ${focusVisible}
`;

/* one-shot actions (pop-out) are NOT toggles: plain icon buttons with
   no active state, so they can't read as "switched off" */
const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: 6px;
  border: 1.5px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  svg path {
    fill: ${colors.textSafe.grey};
    transition: fill 0.15s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.navigationFrame};
  }

  &:hover:not(:disabled) svg path {
    fill: #1a1a1a;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  ${focusVisible}
`;

/* full-size landscape pop-out: a live-synced viewer window at a readable
   size — OS zoom, screen readers and the viewer's own a11y settings all
   work in it */
const openFullSizePreview = (hostId: string) => {
  const previewWidth = Math.round(window.screen.availWidth * 0.6);
  const previewHeight = Math.round((previewWidth / 16) * 9);
  const left = window.screen.availWidth - previewWidth;
  const windowFeatures = `left=${left},top=10,width=${previewWidth},height=${previewHeight}`;
  const url = `${clientConfig.mobilePreviewUrl()}?hostId=${hostId}&inAuthor=true&pop=true`;
  // popup-with-features can be silently blocked; fall back to a synthesized
  // link click, which user activation always allows (opens as a tab)
  const popup = window.open(url, "", windowFeatures);
  if (!popup) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

function PreviewControlBar() {
  const [showPortraitPreview, setPortrait] = useRecoilState(ShowPortraitPreview);
  const [showLandscapePreview, setLandscape] = useRecoilState(ShowLandscapePreview);
  const hostId = useRecoilValue(HostId);

  const [tooltip, setTooltip] = useState({ x: 0, y: 0, visible: false, text: "" });

  const showTooltip = (e: React.MouseEvent<HTMLButtonElement>, text: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      visible: true,
      text,
    });
  };

  const hideTooltip = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const togglePortrait = () => setPortrait(!showPortraitPreview);
  const toggleLandscape = () => setLandscape(!showLandscapePreview);

  return (
    <>
      <ToggleRow className="preview-display-toggles" role="group" aria-label={text.preview.previewControls}>
        <ToggleButton
          $active={showLandscapePreview}
          aria-pressed={showLandscapePreview}
          onClick={toggleLandscape}
          onMouseEnter={(e) => showTooltip(e, text.preview.desktopPreview)}
          onMouseLeave={hideTooltip}
          aria-label={text.preview.desktopPreview}
          data-testid="preview-in-landscape"
        >
          <Monitor />
        </ToggleButton>
        <ToggleButton
          $active={showPortraitPreview}
          aria-pressed={showPortraitPreview}
          onClick={togglePortrait}
          onMouseEnter={(e) => showTooltip(e, text.preview.mobilePreview)}
          onMouseLeave={hideTooltip}
          aria-label={text.preview.mobilePreview}
          data-testid="preview-in-portrait"
        >
          <Phone />
        </ToggleButton>

        <Divider />

        <ActionButton
          data-testid="full-size-preview-button"
          onClick={() => openFullSizePreview(hostId)}
          onMouseEnter={(e) => showTooltip(e, text.preview.fullSizePreview)}
          onMouseLeave={hideTooltip}
          aria-label={text.preview.fullSizePreviewLabel}
        >
          <FullSizePreview />
        </ActionButton>
      </ToggleRow>

      <Tooltip $x={tooltip.x} $y={tooltip.y} $visible={tooltip.visible}>
        {tooltip.text}
      </Tooltip>
    </>
  );
}

export default PreviewControlBar;
